import type { ReactElement } from "react";
import { ChevronsUpDown } from "lucide-react";
import * as React from "react";
import { cn } from "./cn";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";

interface ComboboxProps {
  readonly value: string | null;
  readonly onValueChange: (value: string | null) => void;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  readonly placeholder?: string;
  readonly searchPlaceholder?: string;
  readonly emptyMessage?: string;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly triggerClassName?: string;
  readonly buttonClassName?: string;
  readonly textClassName?: string;
  readonly iconClassName?: string;
  readonly dropdownClassName?: string;
  readonly itemClassName?: string;
  readonly searchInputClassName?: string;
  readonly align?: "start" | "end" | "center";
  readonly fullWidthDropdown?: boolean;
}

/**
 * Combobox component with search functionality
 * Based on shadcn/ui patterns using Command + Popover
 */
/* eslint-disable max-lines-per-function */
export function Combobox({
  value,
  onValueChange,
  options,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found.",
  disabled = false,
  className,
  triggerClassName,
  buttonClassName,
  textClassName,
  iconClassName,
  dropdownClassName,
  itemClassName,
  searchInputClassName,
  align = "start",
  fullWidthDropdown = false,
}: Readonly<ComboboxProps>): ReactElement {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = React.useMemo(() => {
    if (!search) return options;
    const searchLower = search.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(searchLower));
  }, [options, search]);

  const handleSelect = (selectedValue: string) => {
    if (selectedValue === value) {
      onValueChange(null);
    } else {
      onValueChange(selectedValue);
    }
    setOpen(false);
    setSearch("");
  };

  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open]);

  return (
    <div className={cn(!fullWidthDropdown && "relative", className)}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={cn(
          "flex h-8 w-full px-1 items-center justify-between rounded-md bg-neutral-900/0 text-sm text-white shadow-sm hover:bg-neutral-800/50 disabled:cursor-not-allowed disabled:opacity-50",
          triggerClassName,
          buttonClassName,
        )}
      >
        <span className={cn("text-xs", !selectedOption && "text-neutral-500", textClassName)}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronsUpDown className={cn("ml-2 h-3.5 w-3.5 shrink-0 opacity-50", iconClassName)} />
      </button>

      {open && !disabled && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setOpen(false);
            }}
            aria-label="Close combobox"
          />
          <div
            className={cn(
              "absolute text-xs top-full z-50 mt-1 rounded-md border border-neutral-800/50 bg-neutral-950/0 bshadow-xl",
              fullWidthDropdown ? "left-0 right-0 w-auto" : "w-full min-w-[200px]",
              !fullWidthDropdown && align === "start" && "left-0",
              !fullWidthDropdown && align === "end" && "right-0",
              !fullWidthDropdown && align === "center" && "left-1/2 -translate-x-1/2",
              dropdownClassName,
            )}
          >
            <Command className="border-0">
              <CommandInput
                placeholder={searchPlaceholder}
                value={search}
                onValueChange={setSearch}
                autoFocus
                wrapperClassName={searchInputClassName}
              />
              <CommandList className="mt-4">
                <CommandEmpty>{emptyMessage}</CommandEmpty>
                <CommandGroup>
                  {filteredOptions.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => handleSelect(option.value)}
                      className={itemClassName}
                    >
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        </>
      )}
    </div>
  );
}
